/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce, {current} from 'immer';
import {CVSAssetType, User} from '@ion/core/src/types/entities';
import {BasemapTypes} from '@ion/features/src/maps/types/types';
import AssetObserver from '@ion/services/src/websocket/AssetObserver';
import {getStatus, isWarningStatus} from '@/components/sidesheet/SubscriptionDetails';
import {PageState} from '@/types/pages';
import {parseStringToObject} from '../../utils/ComponentUtils';
import {
  ADD_USER_ERROR,
  ADD_USER_REQUEST,
  ADD_USER_RESPONSE,
  ASSET_DIALOG_SHOW,
  ASSET_UPDATED,
  CLEAR_ASSETS,
  CLEAR_SELECTED_ASSET,
  CLEAR_USERS,
  DELETE_USER_ERROR,
  DELETE_USER_REQUEST,
  DELETE_USER_RESPONSE,
  EDIT_ADMIN_ASSET_ERROR,
  EDIT_ADMIN_ASSET_REQUEST,
  EDIT_ADMIN_ASSET_RESPONSE,
  EDIT_USER_ERROR,
  EDIT_USER_REQUEST,
  EDIT_USER_RESPONSE,
  EMERGENCY_DIALOG_SHOW,
  INITIALIZE_DASHBOARD,
  SET_ASSETS,
  SET_ASSETS_ERROR,
  SET_ASSETS_LOADED,
  SET_CURRENT_STATE_ERROR,
  SET_MAP_BASEMAP,
  SET_MAP_EXTENT,
  SET_MAP_INITIALIZED,
  SET_MAP_ZOOM,
  SET_PAYMENT_URL,
  SET_SELECTED_ASSET,
  SET_SETTINGS,
  SET_SUBSCRIPTION_PLANS,
  SET_USERS,
  SET_USERS_LOADED,
  SUBSCRIPTION_DIALOG_SHOW,
  USER_PROFILE_DIALOG_SHOW
} from '../actions/dashboard';

export interface DashboardState extends PageState {
  assets: {
    all: CVSAssetType[];
    selected: CVSAssetType;
    loaded: boolean;
    error: boolean;
    currentStateError: boolean;
  };
  map: {
    initialized: boolean;
    basemap: BasemapTypes;
    extent: null;
    center: null;
    zoom: number;
  }
  assetDialog: {
    show: boolean;
  };
  users: {
    all: User[];
    loaded: boolean;
  },
  addUser: {
    loading: boolean,
    error: null,
    result: null
  },
  deleteUser: {
    loading: boolean,
    error: null,
    result: null
  },
  edit: {
    loading: boolean,
    error: null,
    result: null
  },
  editUser: {
    loading: boolean,
    error: null,
    result: null
  },
  subscriptionPlans: any[];
  paymentUrl: string;
  settings: null;
  emergencyDialog: {
    show: boolean;
  };
  subscriptionDialog: {
    show: boolean;
  };
  userProfileDialog: {
    show: boolean;
  };
};

// defines the initial state for the reducer ...
export const initialState: DashboardState = {
  isInitialized : false,
  assets        : {
    all               : null,
    selected          : null,
    loaded            : false,
    error             : false,
    currentStateError : false
  },
  map : {
    initialized : false,
    basemap     : BasemapTypes.grayVector,
    extent      : null,
    center      : null,
    zoom        : 6
  },
  assetDialog : {
    show : false
  },
  users : {
    all    : null,
    loaded : false
  },
  addUser : {
    loading : false,
    error   : null,
    result  : null
  },
  deleteUser : {
    loading : false,
    error   : null,
    result  : null
  },
  edit : {
    loading : false,
    error   : null,
    result  : null
  },
  editUser : {
    loading : false,
    error   : null,
    result  : null
  },
  subscriptionPlans : [],
  paymentUrl        : '',
  settings          : null,
  emergencyDialog   : {
    show : false
  },
  subscriptionDialog : {
    show : false
  },
  userProfileDialog : {
    show : false
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_DASHBOARD] : (draft: DashboardState) => {
    draft.isInitialized = true;
  },
  [SET_MAP_BASEMAP] : (draft: DashboardState, {basemap}) => {
    draft.map.basemap = basemap;
  },
  [SET_MAP_INITIALIZED] : (draft: DashboardState) => {
    draft.map.initialized = true;
  },
  [SET_MAP_EXTENT] : (draft: DashboardState, {extent}) => {
    draft.map.extent = extent;
  },
  [SET_MAP_ZOOM] : (draft: DashboardState, {zoom}) => {
    draft.map.zoom = zoom;
  },
  [SET_ASSETS_LOADED] : (draft: DashboardState, {loaded}) => {
    draft.assets.loaded = loaded;
  },
  [SET_ASSETS_ERROR] : (draft: DashboardState, {error}) => {
    draft.assets.error = error;
  },
  [SET_CURRENT_STATE_ERROR] : (draft: DashboardState, {error}) => {
    draft.assets.currentStateError = error;
  },
  [SET_ASSETS] : (draft: DashboardState, {assets}) => {
    draft.assets.all = assets;
  },
  [CLEAR_ASSETS] : (draft: DashboardState) => {
    draft.assets.all = null;
  },
  [EDIT_ADMIN_ASSET_REQUEST] : (draft: DashboardState) => {
    draft.edit.loading = true;
    draft.edit.error = draft.edit.result = null;
  },
  [EDIT_ADMIN_ASSET_RESPONSE] : (draft: DashboardState, {asset}) => {
    draft.edit.loading = false;
    draft.edit.result = asset;
  },
  [EDIT_ADMIN_ASSET_ERROR] : (draft: DashboardState, {error}) => {
    draft.edit.loading = false;
    draft.edit.error = error;
  },
  [SET_USERS_LOADED] : (draft: DashboardState, {loaded}) => {
    draft.users.loaded = loaded;
  },
  [SET_USERS] : (draft: DashboardState, {users}) => {
    draft.users.all = users;
  },
  [CLEAR_USERS] : (draft: DashboardState) => {
    draft.users.all = null;
  },
  [ADD_USER_REQUEST] : (draft: DashboardState) => {
    draft.addUser.loading = true;
    draft.addUser.error = draft.addUser.result = null;
  },
  [ADD_USER_RESPONSE] : (draft: DashboardState, {user}) => {
    draft.addUser.loading = false;
    draft.addUser.result = user;
  },
  [ADD_USER_ERROR] : (draft: DashboardState, {error}) => {
    draft.addUser.loading = false;
    draft.addUser.error = error;
  },
  [EDIT_USER_REQUEST] : (draft: DashboardState) => {
    draft.editUser.loading = true;
    draft.editUser.error = draft.editUser.result = null;
  },
  [EDIT_USER_RESPONSE] : (draft: DashboardState, {user}) => {
    draft.editUser.loading = false;
    draft.editUser.result = user;
  },
  [EDIT_USER_ERROR] : (draft: DashboardState, {error}) => {
    draft.editUser.loading = false;
    draft.editUser.error = error;
  },
  [DELETE_USER_REQUEST] : (draft: DashboardState) => {
    draft.deleteUser.loading = true;
    draft.deleteUser.error = draft.deleteUser.result = null;
  },
  [DELETE_USER_RESPONSE] : (draft: DashboardState, {user}) => {
    draft.deleteUser.loading = false;
    draft.deleteUser.result = user;
  },
  [DELETE_USER_ERROR] : (draft: DashboardState, {error}) => {
    draft.deleteUser.loading = false;
    draft.deleteUser.error = error;
  },
  [SET_SELECTED_ASSET] : (draft: DashboardState, {asset}) => {
    draft.assets.selected = asset;
  },
  [ASSET_UPDATED] : ({assets}: DashboardState, {change, id}) => {
    const index: number = assets.all?.findIndex(asset => asset.id === id);
    if (!isNaN(index)) {
      const previous      = current(assets.all[index]);
      let updated         = AssetObserver.update(previous, change) as CVSAssetType;
      const {currentPlan} = updated?.subscriptionData || {};
      if (isWarningStatus(getStatus(currentPlan))) {
        delete updated.latitude;
        delete updated.longitude;
      } else {
        updated = {...updated, ...parseStringToObject(updated?.address)} as CVSAssetType;
      }
      assets.all.splice(index, 1, updated);
      if (assets.selected?.id === updated?.id) {
        assets.selected = updated;
      }
    }
  },
  [CLEAR_SELECTED_ASSET] : (draft: DashboardState) => {
    draft.assets.selected = null;
  },
  [ASSET_DIALOG_SHOW] : (draft: DashboardState, {show}) => {
    draft.assetDialog.show = show;
  },
  [EMERGENCY_DIALOG_SHOW] : (draft: DashboardState, {show}) => {
    draft.emergencyDialog.show = show;
  },
  [SUBSCRIPTION_DIALOG_SHOW] : (draft: DashboardState, {show}) => {
    draft.subscriptionDialog.show = show;
  },
  [USER_PROFILE_DIALOG_SHOW] : (draft: DashboardState, {show}) => {
    draft.userProfileDialog.show = show;
  },
  [SET_SUBSCRIPTION_PLANS] : (draft: DashboardState, {plans}) => {
    draft.subscriptionPlans = plans;
  },
  [SET_PAYMENT_URL] : (draft: DashboardState, {url}) => {
    draft.paymentUrl = url;
  },
  [SET_SETTINGS] : (draft: DashboardState, {settings}) => {
    draft.settings = settings;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: DashboardState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
